import React, { useState } from "react";
import { motion } from "framer-motion";
import { Home, Store, ClipboardList, TrendingUp, CheckCircle2, ShieldCheck,
  MapPin, Bookmark, GitCompare, ChevronDown, X } from "lucide-react";
import { toast } from "sonner";

const Badge = ({ children }) => (
  <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-gold/15 text-gold border border-gold/20">
    {children}
  </span>
);

const aed = (n) => "AED " + Number(n).toLocaleString();

const Metric = ({ label, value, accent, tone }) => (
  <div className="rounded-lg bg-white/[0.03] border border-white/5 px-2.5 py-1.5">
    <div className="text-[9px] uppercase tracking-wider text-white/40">{label}</div>
    <div className={`text-xs font-semibold mt-0.5 ${tone === "up" ? "text-rose-300" : tone === "down" ? "text-emerald-300" : accent ? "text-gold" : "text-white"}`}>{value}</div>
  </div>
);

// Location visual. Keyless by default (stylized map motif). When a real tile URL is
// provided (DATA_MODE=live + coordinates → server-signed Mapbox static image), it
// upgrades automatically — a config swap, not a redesign.
const MapThumb = ({ label, mapUrl }) => (
  <div className="relative h-24 rounded-lg overflow-hidden border border-white/[0.06] mb-3" data-testid="card-map-thumb">
    {mapUrl ? (
      <img src={mapUrl} alt={label} className="w-full h-full object-cover" />
    ) : (
      <div className="w-full h-full"
        style={{
          backgroundColor: "#0d1b2e",
          backgroundImage:
            "linear-gradient(rgba(184,134,11,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(184,134,11,0.10) 1px, transparent 1px), radial-gradient(circle at 70% 30%, rgba(184,134,11,0.18), transparent 55%)",
          backgroundSize: "22px 22px, 22px 22px, 100% 100%",
        }}
      />
    )}
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="flex items-center gap-1.5 text-xs text-white/90 bg-black/40 backdrop-blur-sm px-2.5 py-1 rounded-full border border-white/10">
        <MapPin size={12} className="text-gold" /> {label}
      </div>
    </div>
  </div>
);

const Shell = ({ children, i = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: i * 0.06, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    className="rounded-xl bg-surface/70 border border-white/[0.06] p-4 hover:border-gold/30 transition-colors duration-300"
    data-testid="ai-result-card"
  >
    {children}
  </motion.div>
);

const ComparablesCard = ({ card, i }) => {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState([]);
  const [compare, setCompare] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const dev = card.deviation_pct;
  const overpriced = dev != null && dev > 0;
  const posLabel = dev == null ? null
    : Math.abs(dev) <= 3 ? "At market"
    : overpriced ? `${dev}% above comps` : `${Math.abs(dev)}% below comps`;

  const toggle = (k) =>
    setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : s.length >= 3 ? s : [...s, k]));

  const save = () => {
    try {
      const key = "buydubai_saved";
      const cur = JSON.parse(localStorage.getItem(key) || "[]");
      cur.push({ kind: "cma", area: card.area, building: card.building, median_psf: card.median_psf,
        range: card.suggested_range_aed, at: Date.now() });
      localStorage.setItem(key, JSON.stringify(cur));
      toast.success("Saved to your items");
    } catch { toast.error("Could not save"); }
  };

  return (
    <Shell i={i}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-gold min-w-0">
          <TrendingUp size={16} className="shrink-0" />
          <span className="text-sm font-semibold text-white truncate">Comparable sales · {card.building ? `${card.building}, ` : ""}{card.area}</span>
        </div>
        {card.badge && <Badge>{card.badge}</Badge>}
      </div>

      <MapThumb label={`${card.building ? card.building + " · " : ""}${card.area}`} mapUrl={card.map_url} />

      {posLabel && (
        <div className="mb-3">
          <span data-testid="price-position-badge" className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${
            Math.abs(dev) <= 3 ? "bg-white/5 text-white/70 border-white/10"
            : overpriced ? "bg-rose-500/10 text-rose-300 border-rose-500/25"
            : "bg-emerald-500/10 text-emerald-300 border-emerald-500/25"}`}>
            {posLabel}
          </span>
        </div>
      )}

      {card.median_psf != null && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3" data-testid="cma-card-stats">
          <Metric label="Median AED/sqft" value={Number(card.median_psf).toLocaleString()} accent />
          {card.subject_psf != null && <Metric label="Subject AED/sqft" value={Number(card.subject_psf).toLocaleString()} />}
          {dev != null && <Metric label="Deviation" value={`${dev > 0 ? "+" : ""}${dev}%`} tone={Math.abs(dev) <= 3 ? null : overpriced ? "up" : "down"} />}
          {card.confidence && <Metric label="Confidence" value={String(card.confidence).replace(/^./, (c) => c.toUpperCase())} />}
        </div>
      )}

      {Array.isArray(card.suggested_range_aed) && (
        <div className="mb-3 rounded-lg bg-gold/5 border border-gold/15 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-white/40">Suggested range</div>
          <div className="font-display text-gold text-sm">{aed(card.suggested_range_aed[0])} — {aed(card.suggested_range_aed[1])}</div>
        </div>
      )}

      <div className="space-y-1.5">
        {card.items.map((it, k) => (
          <label key={k} className="flex items-center gap-2 text-xs text-white/70 border-b border-white/5 pb-1.5 cursor-pointer">
            {compare && (
              <input type="checkbox" checked={sel.includes(k)} onChange={() => toggle(k)}
                data-testid="compare-checkbox" className="accent-gold w-3.5 h-3.5" />
            )}
            <span className="flex-1">{it.building}{it.bedrooms ? ` · ${it.bedrooms}BR` : ""} · {it.size_sqft} sqft</span>
            <span className="text-white">{aed(it.price)} <span className="text-white/40">{it.aed_per_sqft ? `· ${Number(it.aed_per_sqft).toLocaleString()}/sqft ` : ""}· {it.date}</span></span>
          </label>
        ))}
      </div>

      {/* Why this? expander */}
      <button onClick={() => setOpen((o) => !o)} data-testid="why-this-toggle"
        className="mt-3 inline-flex items-center gap-1 text-[11px] text-gold/90 hover:text-gold transition-colors">
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} /> Why this?
      </button>
      {open && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
          className="mt-2 text-[11px] text-white/55 leading-relaxed" data-testid="why-this-panel">
          Selected via a deterministic engine: {card.comp_count} qualifying transactions, level {card.level_used || "—"} ({card.confidence}) confidence.
          Each comparable matches building/bedroom and falls inside the size and recency window; median and range are computed with exact arithmetic. The AI never invents a number.
        </motion.div>
      )}

      {/* Actions */}
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={save} data-testid="card-save-btn"
          className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg border border-white/10 text-white/75 hover:border-gold/40 hover:text-gold transition-colors">
          <Bookmark size={13} /> Save
        </button>
        <button onClick={() => { setCompare((c) => !c); setSel([]); }} data-testid="card-compare-btn"
          className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors ${compare ? "border-gold/40 text-gold" : "border-white/10 text-white/75 hover:border-gold/40 hover:text-gold"}`}>
          <GitCompare size={13} /> Compare
        </button>
        {compare && sel.length >= 2 && (
          <button onClick={() => setCompareOpen(true)} data-testid="open-compare-btn"
            className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg bg-gold text-ink font-semibold">
            View {sel.length} side by side
          </button>
        )}
      </div>

      {card.demo_line && <p className="mt-3 text-[11px] text-white/40" data-testid="cma-card-demo-line">{card.demo_line}</p>}

      <CompareModal open={compareOpen && sel.length >= 2} rows={sel.map((k) => card.items[k])} onClose={() => setCompareOpen(false)} />
    </Shell>
  );
};

// Compare modal rendered outside the card selection flow
const CompareModal = ({ open, rows, onClose }) => {
  if (!open) return null;
  const cols = [
    ["Building", (r) => r.building],
    ["Beds", (r) => (r.bedrooms ? `${r.bedrooms}BR` : "—")],
    ["Size", (r) => `${r.size_sqft} sqft`],
    ["Price", (r) => aed(r.price)],
    ["AED/sqft", (r) => (r.aed_per_sqft ? Number(r.aed_per_sqft).toLocaleString() : "—")],
    ["Date", (r) => r.date],
  ];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="compare-modal">
      <div className="w-full max-w-2xl rounded-2xl bg-surface border border-white/10 p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="text-white font-semibold text-sm">Side-by-side comparison</div>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="compare-close"><X size={18} /></button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <tbody>
              {cols.map(([label, fn]) => (
                <tr key={label} className="border-b border-white/5">
                  <td className="py-2 pr-3 text-white/40 uppercase tracking-wider text-[10px] whitespace-nowrap">{label}</td>
                  {rows.map((r, i) => <td key={i} className="py-2 px-3 text-white">{fn(r)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export const ResultCard = ({ card, i }) => {
  if (card.type === "comparables") return <ComparablesCard card={card} i={i} />;

  if (card.type === "property")
    return (
      <Shell i={i}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 text-gold"><Home size={16} /><span className="text-sm font-semibold text-white">{card.title}</span></div>
          {card.badge && <Badge>{card.badge}</Badge>}
        </div>
        <div className="mt-1 text-xs text-white/50">{card.area} · {card.bedrooms} BR · {card.size_sqft} sqft</div>
        <div className="mt-2 font-display text-xl text-gold">{aed(card.price)}</div>
        {card.highlights && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {card.highlights.map((h) => <span key={h} className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-white/70">{h}</span>)}
          </div>
        )}
      </Shell>
    );

  if (card.type === "business")
    return (
      <Shell i={i}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 text-gold"><Store size={16} /><span className="text-sm font-semibold text-white">{card.name}</span></div>
          {card.badge && <Badge>{card.badge}</Badge>}
        </div>
        <div className="mt-1 text-xs text-white/50">{card.category} · {card.area}</div>
        <div className="mt-2 flex items-center gap-3 text-xs text-white/70">
          <span className="text-gold">★ {card.rating}</span>
          {card.verified && <span className="flex items-center gap-1 text-emerald-400"><ShieldCheck size={12} /> Verified</span>}
          <span>Responds ~{card.response_time}</span>
        </div>
      </Shell>
    );

  if (card.type === "quotes")
    return (
      <Shell i={i}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-gold"><ClipboardList size={16} /><span className="text-sm font-semibold text-white">Quotes · {card.service}</span></div>
          {card.badge && <Badge>{card.badge}</Badge>}
        </div>
        <div className="space-y-2">
          {card.providers.map((p) => (
            <div key={p.name} className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2">
              <div>
                <div className="text-sm text-white">{p.name}</div>
                <div className="text-[11px] text-white/50">{p.availability} · {p.warranty} warranty · score {p.verified_score}</div>
              </div>
              <div className="text-right">
                <div className="text-gold font-semibold text-sm">{p.price}</div>
                <button className="mt-1 text-[11px] px-2 py-0.5 rounded bg-gold/15 text-gold border border-gold/20 hover:bg-gold/25">Select</button>
              </div>
            </div>
          ))}
        </div>
      </Shell>
    );

  return (
    <Shell i={i}>
      <div className="flex items-center gap-2 text-white/70 text-sm"><CheckCircle2 size={16} className="text-emerald-400" /> {JSON.stringify(card)}</div>
    </Shell>
  );
};
